import React, { useCallback, useEffect, useState } from 'react';
import { Container, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import api from '../services/api';
import { KLIC_NOVY_KONTAKT } from './CustomerDetail';
import { confirm } from '../components/ConfirmDialog';
import './CustomerForm.css';

// Údaje z company_info, které mají ve formuláři vlastní pole. Zbytek se
// vypíše pod rozbalovátkem "Další údaje" genericky, stejně jako v detailu.
const HLAVNI_INFO = ['ID1', 'ID2', 'MaturityDay', 'CommunicationMethod', 'BankAccountNumber', 'IBAN'];

// Způsob zasílání — hodnoty odpovídají tomu, co je v datech ze starého systému.
const KOMUNIKACE = [
  { value: 'EMAIL', label: 'E-mailem' },
  { value: 'POST', label: 'Poštou' },
  { value: 'EXTERNAL_SYSTEM', label: 'Externí systém' },
];

// ARES vrací dvoupísmenný kód, `country` má ale jen názvy. Firmy z ARESu
// jsou vždycky české, ostatní se doplní ručně v detailu.
const ZEME_PODLE_KODU = { CZ: 2, SK: 1 };

// Adresa z ARESu je sídlo firmy — v číselníku address_type je to kód `Main`.
// Fakturace na ni dosáhne, resolveAddress() na `Main` spadá jako na záložní typ.
const PRAZDNA_ADRESA = {
  type: 'Main',
  name: '',
  street: '',
  city: '',
  postal_code: '',
  country_id: 2,
  is_valid: true,
};

// Pole sídla, která mají být vyplněná. Uložit firmu s neúplnou adresou jde,
// ale až po potvrzení — bez adresy není na co vystavit fakturu.
const POVINNA_ADRESA = [
  { key: 'street', label: 'ulice' },
  { key: 'postal_code', label: 'PSČ' },
  { key: 'city', label: 'město' },
];

// Výchozí hodnoty pro novou firmu — co se v drtivé většině případů stejně vyplní.
const VYCHOZI_INFO = {
  MaturityDay: '14',
  CommunicationMethod: 'EMAIL',
};

const PRAZDNA_FIRMA = {
  company_name: '',
  secondary_name: '',
  homepage_url: '',
  state: 'active',
  note: '',
};

function CustomerForm({ openTab, closeTab, activeTab }) {
  const [firma, setFirma] = useState(PRAZDNA_FIRMA);
  const [info, setInfo] = useState(VYCHOZI_INFO);
  const [enums, setEnums] = useState(null);

  const [ico, setIco] = useState('');
  const [aresNacita, setAresNacita] = useState(false);
  const [aresChyba, setAresChyba] = useState(null);
  // Fakturační adresa. Vyplní ji buď ARES, nebo uživatel ručně; založí se
  // až po vytvoření firmy. Prázdnou adresu nezakládáme.
  const [adresa, setAdresa] = useState(PRAZDNA_ADRESA);
  // Nevyplněná pole sídla, na která jsme se už ptali — zůstanou zvýrazněná,
  // dokud je uživatel nedoplní.
  const [adresaChybi, setAdresaChybi] = useState([]);

  // Už existující firma se stejným IČ. Když je nastavená, uložení blokujeme.
  const [duplicita, setDuplicita] = useState(null);

  const [uklada, setUklada] = useState(false);
  const [chyba, setChyba] = useState(null);
  const [dalsiOtevrene, setDalsiOtevrene] = useState(false);

  useEffect(() => {
    api.getCrmEnums()
      .then((res) => setEnums(res.data))
      .catch((err) => setChyba('Nepodařilo se načíst číselníky: ' + err.message));
  }, []);

  const zavri = useCallback(() => {
    if (closeTab && activeTab) closeTab(activeTab);
  }, [closeTab, activeTab]);

  const nastavInfo = (klic, hodnota) => setInfo((p) => ({ ...p, [klic]: hodnota }));

  const nastavAdresu = (klic, hodnota) => {
    setAdresa((p) => ({ ...p, [klic]: hodnota }));
    if (hodnota.trim()) setAdresaChybi((p) => p.filter((k) => k !== klic));
  };

  // ── Kontrola duplicit ─────────────────────────────────────────────────
  const zkontrolujIco = useCallback(async (hodnota) => {
    const cisto = (hodnota || '').replace(/\D+/g, '');
    if (!cisto) {
      setDuplicita(null);
      return null;
    }
    try {
      const res = await api.findCompanyByIco(cisto);
      const nalezena = res.data?.company || null;
      setDuplicita(nalezena);
      return nalezena;
    } catch (err) {
      // Nedostupná kontrola nesmí blokovat práci — duplicitu stejně
      // odchytí backend při ukládání.
      console.warn('[CustomerForm] kontrola IČ selhala:', err);
      setDuplicita(null);
      return null;
    }
  }, []);

  const otevriExistujici = () => {
    if (!duplicita) return;
    openTab(
      `customer-${duplicita.id}`,
      duplicita.company_name,
      `/customer/${duplicita.id}`,
      'CustomerDetail',
      { customerId: duplicita.id },
    );
  };

  // ── ARES ──────────────────────────────────────────────────────────────
  const nactiZAresu = async () => {
    const cisto = ico.replace(/\s/g, '');
    if (!cisto) {
      setAresChyba('Nejdřív vyplň IČ.');
      return;
    }

    setAresNacita(true);
    setAresChyba(null);
    try {
      // Když firma v systému je, nemá smysl tahat ARES — rovnou to řekneme.
      if (await zkontrolujIco(cisto)) {
        return;
      }

      const res = await api.lookupAres(cisto);
      const d = res.data || {};

      setFirma((p) => ({ ...p, company_name: d.company_name || p.company_name }));
      setInfo((p) => ({ ...p, ID1: d.ico || cisto, ID2: d.dic || p.ID2 || '' }));
      setAdresa((p) => ({
        ...p,
        name: d.company_name || p.name,
        street: d.street || '',
        city: d.city || '',
        postal_code: d.postal_code || '',
        country_id: ZEME_PODLE_KODU[d.country] || 2,
      }));
      setAdresaChybi([]);
    } catch (err) {
      setAresChyba(err.message || 'Firmu se nepodařilo v ARESu najít.');
    } finally {
      setAresNacita(false);
    }
  };

  // ── Uložení ───────────────────────────────────────────────────────────
  const uloz = async () => {
    if (!firma.company_name.trim()) {
      setChyba('Vyplň název firmy.');
      return;
    }

    // Poslední kontrola těsně před uložením — IČ mohlo přibýt až po načtení
    // formuláře nebo ho mezitím založil někdo jiný.
    if (await zkontrolujIco(info.ID1 || ico)) {
      setChyba(null);
      return;
    }

    // Neúplné sídlo nechceme tiše uložit — ptáme se, ale rozhodnutí necháme
    // na uživateli (u zahraničních firem adresu z ARESu nevytáhneme).
    const chybi = POVINNA_ADRESA.filter((p) => !(adresa[p.key] || '').trim());
    if (chybi.length) {
      setAdresaChybi(chybi.map((p) => p.key));
      const potvrzeno = await confirm({
        title: chybi.length === POVINNA_ADRESA.length ? 'Firma nemá vyplněné sídlo' : 'Adresa sídla není kompletní',
        message: chybi.length === POVINNA_ADRESA.length
          ? 'Opravdu firmu uložit bez adresy?'
          : `Chybí ${chybi.map((p) => p.label).join(' a ')}. Opravdu firmu uložit s neúplnou adresou?`,
        variant: 'warning',
        icon: 'bi-exclamation-triangle',
        confirmText: 'Uložit i tak',
        cancelText: 'Doplnit adresu',
      });
      if (!potvrzeno) {
        setChyba(null);
        return;
      }
    }

    setUklada(true);
    setChyba(null);
    try {
      // Prázdné hodnoty do company_info neposíláme, ať tam nevznikají prázdné řádky.
      const infoKUlozeni = Object.fromEntries(
        Object.entries(info).filter(([, v]) => v !== '' && v !== null && v !== undefined),
      );

      const res = await api.createCompany({ ...firma, info: infoKUlozeni });
      const id = res.data?.id;
      if (!id) throw new Error('Server nevrátil ID nové firmy.');

      // Adresa je doplněk — když se nepovede, firma už existuje a nemá smysl
      // celé uložení shodit. Uživateli to řekneme až v detailu.
      const adresaVyplnena = [adresa.street, adresa.city, adresa.postal_code]
        .some((v) => (v || '').trim() !== '');

      if (adresaVyplnena) {
        try {
          await api.createAddress({ ...adresa, company_id: id });
        } catch (err) {
          console.warn('[CustomerForm] fakturační adresu se nepodařilo založit:', err);
        }
      }

      // Firma bez kontaktní osoby je k ničemu (bez ní se nedá fakturovat),
      // tak se na ni rovnou zeptáme a detail otevřeme s připraveným formulářem.
      const chceKontakt = await confirm({
        title: 'Firma je uložená',
        message: 'Chcete k ní rovnou založit kontaktní osobu?',
        icon: 'bi-person-plus',
        confirmText: 'Založit kontakt',
        cancelText: 'Teď ne',
      });
      if (chceKontakt) {
        sessionStorage.setItem(KLIC_NOVY_KONTAKT, String(id));
      }

      openTab(`customer-${id}`, firma.company_name, `/customer/${id}`, 'CustomerDetail', { customerId: id });
      zavri();
    } catch (err) {
      setChyba('Nepodařilo se uložit: ' + err.message);
      setUklada(false);
    }
  };

  // ── Způsob zasílání ───────────────────────────────────────────────────
  // Ve starých datech jsou i kombinace ("POST,EMAIL"). Když taková hodnota
  // přijde, přidáme ji do nabídky, aby ji editace nesmazala.
  const komunikaceHodnota = info.CommunicationMethod || '';
  const komunikaceVolby = KOMUNIKACE.some((k) => k.value === komunikaceHodnota) || !komunikaceHodnota
    ? KOMUNIKACE
    : [...KOMUNIKACE, { value: komunikaceHodnota, label: komunikaceHodnota }];

  const dalsiTypy = (enums?.companyInfoTypes || []).filter((t) => !HLAVNI_INFO.includes(t.id));

  return (
    <Container fluid className="px-4 py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Nová firma</h4>
        <div>
          <Button variant="outline-secondary" className="me-2" onClick={zavri} disabled={uklada}>
            <i className="bi bi-x-lg"></i> Zrušit
          </Button>
          <Button variant="primary" onClick={uloz} disabled={uklada || !!duplicita}>
            {uklada
              ? <><Spinner as="span" size="sm" animation="border" className="me-2" />Ukládám…</>
              : <><i className="bi bi-check-lg"></i> Uložit a pokračovat</>}
          </Button>
        </div>
      </div>

      {chyba && <Alert variant="danger" onClose={() => setChyba(null)} dismissible>{chyba}</Alert>}

      <div className="form-section">
        <div className="section-title">Základní údaje</div>

        <Row className="mb-3">
          <Col md={4}>
            <Form.Label>IČ</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                value={ico}
                onChange={(e) => {
                  setIco(e.target.value);
                  nastavInfo('ID1', e.target.value);
                  if (duplicita) setDuplicita(null);
                }}
                onBlur={(e) => zkontrolujIco(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); nactiZAresu(); } }}
                isInvalid={!!duplicita}
              />
              <Button variant="outline-primary" onClick={nactiZAresu} disabled={aresNacita} className="text-nowrap">
                {aresNacita
                  ? <Spinner as="span" size="sm" animation="border" />
                  : <><i className="bi bi-download me-1"></i>ARES</>}
              </Button>
            </div>
          </Col>
          <Col md={5}>
            <Form.Label>Název firmy <span className="required-star">*</span></Form.Label>
            <Form.Control
              type="text"
              value={firma.company_name}
              onChange={(e) => setFirma((p) => ({ ...p, company_name: e.target.value }))}
            />
          </Col>
          <Col md={3}>
            <Form.Label>DIČ</Form.Label>
            <Form.Control
              type="text"
              value={info.ID2 || ''}
              onChange={(e) => nastavInfo('ID2', e.target.value)}
            />
          </Col>
        </Row>

        {duplicita && (
          <Alert variant="danger" className="d-flex align-items-center justify-content-between gap-3">
            <div>
              <i className="bi bi-exclamation-octagon-fill me-2"></i>
              Firma s tímto IČ už v systému existuje — <strong>{duplicita.company_name}</strong>
              {duplicita.state === 'inactive' && <span className="ms-1">(neaktivní)</span>}
            </div>
            <Button variant="outline-danger" size="sm" className="text-nowrap" onClick={otevriExistujici}>
              Otevřít firmu
            </Button>
          </Alert>
        )}

        {aresChyba && (
          <Alert variant="warning" className="py-2" onClose={() => setAresChyba(null)} dismissible>
            {aresChyba} Údaje jde vyplnit i ručně.
          </Alert>
        )}

        <Row className="mb-3">
          <Col md={4}>
            <Form.Label>Doplňkový název</Form.Label>
            <Form.Control
              type="text"
              value={firma.secondary_name}
              onChange={(e) => setFirma((p) => ({ ...p, secondary_name: e.target.value }))}
            />
          </Col>
          <Col md={5}>
            <Form.Label>Web</Form.Label>
            <Form.Control
              type="text"
              value={firma.homepage_url}
              onChange={(e) => setFirma((p) => ({ ...p, homepage_url: e.target.value }))}
            />
          </Col>
          <Col md={3}>
            <Form.Label>Stav</Form.Label>
            <Form.Select
              value={firma.state}
              onChange={(e) => setFirma((p) => ({ ...p, state: e.target.value }))}
            >
              <option value="active">Aktivní</option>
              <option value="inactive">Neaktivní</option>
            </Form.Select>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <Form.Label>Poznámka</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={firma.note}
              onChange={(e) => setFirma((p) => ({ ...p, note: e.target.value }))}
            />
          </Col>
        </Row>
      </div>

      <div className="form-section">
        <div className="section-title">Fakturační údaje</div>

        <Row className="mb-3">
          <Col md={3}>
            <Form.Label>Splatnost</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                type="number"
                min={0}
                max={365}
                value={info.MaturityDay || ''}
                onChange={(e) => nastavInfo('MaturityDay', e.target.value)}
              />
              <span className="text-muted text-nowrap">dní</span>
            </div>
          </Col>
          <Col md={3}>
            <Form.Label>Způsob zasílání</Form.Label>
            <Form.Select
              value={komunikaceHodnota}
              onChange={(e) => nastavInfo('CommunicationMethod', e.target.value)}
            >
              <option value="">—</option>
              {komunikaceVolby.map((k) => (
                <option key={k.value} value={k.value}>{k.label}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label>Číslo účtu</Form.Label>
            <Form.Control
              type="text"
              value={info.BankAccountNumber || ''}
              onChange={(e) => nastavInfo('BankAccountNumber', e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label>IBAN</Form.Label>
            <Form.Control
              type="text"
              value={info.IBAN || ''}
              onChange={(e) => nastavInfo('IBAN', e.target.value)}
            />
          </Col>
        </Row>

        {dalsiTypy.length > 0 && (
          <>
            <button
              type="button"
              className="btn-add-item btn btn-link p-0 border-0 align-baseline text-decoration-none"
              onClick={() => setDalsiOtevrene((v) => !v)}
              aria-expanded={dalsiOtevrene}
            >
              <i className={`bi bi-chevron-${dalsiOtevrene ? 'down' : 'right'} me-1`}></i>
              Další údaje ({dalsiTypy.length})
            </button>

            {dalsiOtevrene && (
              <Row className="mt-3">
                {dalsiTypy.map((t) => (
                  <Col md={4} key={t.id} className="mb-3">
                    <Form.Label className="small">{t.label}</Form.Label>
                    <Form.Control
                      size="sm"
                      value={info[t.id] || ''}
                      onChange={(e) => nastavInfo(t.id, e.target.value)}
                    />
                  </Col>
                ))}
              </Row>
            )}
          </>
        )}
      </div>

      <div className="form-section">
        <div className="section-title">Sídlo</div>
        <Row>
          <Col md={6}>
            <Form.Label>Ulice</Form.Label>
            <Form.Control
              value={adresa.street}
              isInvalid={adresaChybi.includes('street')}
              onChange={(e) => nastavAdresu('street', e.target.value)}
            />
            <Form.Text className="text-muted">
              U obcí bez ulic sem patří název obce a číslo popisné.
            </Form.Text>
          </Col>
          <Col md={3}>
            <Form.Label>PSČ</Form.Label>
            <Form.Control
              value={adresa.postal_code}
              isInvalid={adresaChybi.includes('postal_code')}
              onChange={(e) => nastavAdresu('postal_code', e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label>Město</Form.Label>
            <Form.Control
              value={adresa.city}
              isInvalid={adresaChybi.includes('city')}
              onChange={(e) => nastavAdresu('city', e.target.value)}
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col md={6}>
            <Form.Label>Země</Form.Label>
            <Form.Select
              value={adresa.country_id || ''}
              onChange={(e) => setAdresa((p) => ({
                ...p,
                country_id: e.target.value ? parseInt(e.target.value, 10) : null,
              }))}
            >
              <option value="">—</option>
              {(enums?.countries || []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      </div>

      <div className="d-flex justify-content-end gap-2 mt-4">
        <Button variant="outline-secondary" onClick={zavri} disabled={uklada}>
          <i className="bi bi-x-lg"></i> Zrušit
        </Button>
        <Button variant="primary" onClick={uloz} disabled={uklada || !!duplicita}>
          {uklada
            ? <><Spinner as="span" size="sm" animation="border" className="me-2" />Ukládám…</>
            : <><i className="bi bi-check-lg"></i> Uložit a pokračovat</>}
        </Button>
      </div>
    </Container>
  );
}

export default CustomerForm;
